"use client";

import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  X,
  Globe,
  MapPin,
  Check,
  AlertCircle,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { HttpTypes } from "@medusajs/types";

import { useCustomer } from "@/contexts/customer-context";
import { useCart } from "@/contexts/cart-context";
import { cn } from "@/lib/util/utils";
import { listRegions } from "@/lib/data/regions";
import { listAvailableLocations } from "@/lib/data/locations";
import { setCountryCookie } from "@/lib/util/country";
import { DEFAULT_COUNTRY_CODE } from "@/lib/constants";
import {
  cubaLocations,
  cubaProvinceToIso,
  cubaIsoToProvinceName,
  type Province as CubaProvince,
} from "@/lib/cuba-locations";
import { updateCustomer } from "@/modules/customer/actions/customer";
import { clearCart } from "@/modules/cart/actions/cart";
import { useRegionStore, useLocationStore } from "@/store/index";
import Dialog from "../dialog";
import logger from "@/lib/util/logger";

type Step = "country" | "province" | "municipality";

export function LocationSelectionModal() {
  const { refreshCart } = useCart();
  const { customer } = useCustomer();

  const {
    region,
    setRegion,
    countryCode,
    setCountryCode,
    isRegionModalOpen,
    setRegionModalOpen,
    hasSelectedRegion,
  } = useRegionStore();

  const {
    province,
    provinceName,
    municipality,
    setProvince,
    setMunicipality,
    setHasSelectedLocation,
  } = useLocationStore();

  const [step, setStep] = useState<Step>("country");
  const [selectedRegion, setSelectedRegion] =
    useState<HttpTypes.StoreRegion | null>(null);
  const [selectedCountry, setSelectedCountry] =
    useState<HttpTypes.StoreRegionCountry | null>(null);
  const [selectedStateIso, setSelectedStateIso] = useState("");
  const [selectedStateName, setSelectedStateName] = useState("");
  const [selectedMunicipality, setSelectedMunicipality] = useState("");

  const { hasSelectedLocation } = useLocationStore();
  const isForcedMode = !hasSelectedRegion || !hasSelectedLocation;
  const isCuba = selectedCountry?.iso_2?.toLowerCase() === "cu";

  const { data: regions, isLoading: regionsLoading } = useQuery({
    queryKey: ["regions"],
    queryFn: listRegions,
  });

  logger.info("LocationSelectionModal - regions loaded", { regions, regionsLoading });

  const { data: availableLocations } = useQuery({
    queryKey: ["available-locations"],
    queryFn: listAvailableLocations,
  });

  logger.info("LocationSelectionModal - available locations loaded", { availableLocations });

  const availableCountryCodes = useMemo(
    () => new Set(availableLocations?.map((c) => c.code) ?? []),
    [availableLocations]
  );

  const filteredRegions = useMemo(() => {
    const mapped =
      regions?.map((r) => ({
        ...r,
        countries: r.countries?.filter((c) =>
          availableCountryCodes.has(c.iso_2?.toLowerCase() || "")
        ),
      })) ?? [];
    return mapped.filter((r) => (r.countries?.length ?? 0) > 0);
  }, [regions, availableCountryCodes]);

  const currentCountryLocations = useMemo(
    () =>
      availableLocations?.find(
        (c) => c.code === (selectedCountry?.iso_2 || "").toLowerCase()
      ),
    [availableLocations, selectedCountry]
  );

  const cubaProvinces = useMemo(
    () => Object.keys(cubaLocations) as CubaProvince[],
    []
  );

  const stateList = useMemo(() => {
    if (!selectedCountry) return [];
    if (isCuba) {
      const availableIsoCodes = new Set(
        currentCountryLocations?.states.map((s) => s.iso_code) ?? []
      );
      return cubaProvinces
        .filter((name) => availableIsoCodes.has(cubaProvinceToIso[name]))
        .map((name) => ({
          name,
          iso_code: cubaProvinceToIso[name] || "",
        }));
    }
    return currentCountryLocations?.states ?? [];
  }, [isCuba, currentCountryLocations, cubaProvinces, selectedCountry]);

  const municipalityList = useMemo(() => {
    if (!isCuba || !selectedStateIso) return [];
    const provinceName = cubaIsoToProvinceName[selectedStateIso];
    if (!provinceName) return [];
    return (cubaLocations as Record<string, string[]>)[provinceName] || [];
  }, [isCuba, selectedStateIso]);

  // Sync internal state when modal opens
  useEffect(() => {
    if (!isRegionModalOpen) return;

    if (region) setSelectedRegion(region);
    if (countryCode && region?.countries) {
      const c = region.countries.find((c) => c.iso_2 === countryCode);
      if (c) setSelectedCountry(c);
    }
    if (province) {
      setSelectedStateIso(province);
      setSelectedStateName(provinceName || "");
    }
    if (municipality) {
      setSelectedMunicipality(municipality);
    }

    setStep("country");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRegionModalOpen]);

  const totalSteps = isCuba ? 3 : 2;
  const currentStepIndex =
    step === "country" ? 1 : step === "province" ? 2 : 3;

  const handleCountrySelect = (
    r: HttpTypes.StoreRegion,
    c: HttpTypes.StoreRegionCountry
  ) => {
    const isSameCountry = selectedCountry?.iso_2 === c.iso_2;
    setSelectedRegion(r);
    setSelectedCountry(c);
    if (!isSameCountry) {
      setSelectedStateIso("");
      setSelectedStateName("");
      setSelectedMunicipality("");
    }
  };

  const handleStateChange = (isoCode: string) => {
    const state = stateList.find((s) => s.iso_code === isoCode);
    const isSameState = selectedStateIso === isoCode;
    setSelectedStateIso(isoCode);
    setSelectedStateName(state?.name || "");
    if (!isSameState) {
      setSelectedMunicipality("");
    }
  };

  const handleConfirm = async () => {
    if (!selectedRegion || !selectedCountry) return;
    if (!selectedStateIso) return;
    if (isCuba && !selectedMunicipality) return;

    setRegion(selectedRegion);
    setCountryCode(selectedCountry.iso_2 ?? null);

    setProvince(selectedStateIso, selectedStateName);
    if (isCuba) {
      setMunicipality(selectedMunicipality);
    } else {
      setMunicipality("");
    }
    setHasSelectedLocation(true);
    setRegionModalOpen(false);

    // Persist the choice so the server (proxy.ts) can redirect country-less
    // requests straight to this country on the next cold entry.
    const iso2 = selectedCountry.iso_2?.toLowerCase() || DEFAULT_COUNTRY_CODE;
    setCountryCookie(iso2);

    // No router.push here: CountryCodeSync reacts to the countryCode change
    // above and router.replace()s the URL to match — a single navigation. A
    // push() here too raced with it and intermittently left the OLD country in
    // the URL. Also don't block on the cart/customer round-trip ("won't work on
    // slow internet"); the store is already updated so the destination is right.
    try {
      await clearCart();
      await refreshCart();
    } catch (error) {
      logger.error("Cart refresh after region switch failed", error);
    }

    if (customer) {
      const meta: Record<string, unknown> = {
        ...customer.metadata,
        country_code: selectedCountry.iso_2,
        province: selectedStateIso,
        province_name: selectedStateName,
      };
      if (isCuba && selectedMunicipality) {
        meta.municipality = selectedMunicipality;
      } else {
        delete meta.municipality;
      }
      // Fire-and-forget: a slow metadata write must not freeze the switch.
      updateCustomer({ metadata: meta }).catch((error) =>
        logger.error("Customer metadata sync after region switch failed", error)
      );
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (isForcedMode && !open) return;
    setRegionModalOpen(open);
  };

  const canConfirm =
    selectedStateIso && (!isCuba || selectedMunicipality);

  const canGoToProvince = !!selectedCountry;
  const canGoToMunicipality = !!selectedStateIso;

  return (
    <Dialog
      open={isRegionModalOpen}
      onOpenChange={handleOpenChange}
      showCloseButton={false}
      contentClassName={cn(
        "bg-cream dark:bg-cream rounded-2xl shadow-xl max-w-sm sm:max-w-md",
        isForcedMode && "border-2 border-orange-main"
      )}
      size="md"
    >
      {/* Close button */}
      {!isForcedMode && (
        <button
          onClick={() => setRegionModalOpen(false)}
          className="text-green-main absolute top-4 right-4 hover:text-[#047a2e]"
        >
          <X className="h-6 w-6" />
        </button>
      )}

      {/* Icon */}
      <div className="mb-4 flex justify-center">
        <div
          className={cn(
            "flex h-16 w-16 items-center justify-center rounded-2xl",
            isForcedMode ? "bg-orange-main" : "bg-green-main"
          )}
        >
          {step === "country" ? (
            <Globe className="h-8 w-8 text-white" />
          ) : (
            <MapPin className="h-8 w-8 text-white" />
          )}
        </div>
      </div>

      {/* Title */}
      {!isForcedMode && (
        <p className="text-center text-sm">Debes escoger la ubicación destino</p>
      )}
      <h2 className="font-inter mb-1 text-center text-lg font-normal text-gray-800">
        {isForcedMode
          ? "Bienvenido a Carambola Online"
          : step === "country"
          ? "Selecciona el país"
          : step === "province"
          ? isCuba
            ? "Selecciona la provincia"
            : "Selecciona el estado"
          : "Selecciona el municipio"}
      </h2>
      <p className="font-inter mb-4 text-center text-sm text-gray-600">
        {isForcedMode
          ? "Para comenzar a comprar, selecciona el destino de tu envío."
          : `Paso ${currentStepIndex} de ${totalSteps}`}
      </p>

      {/* Progress bar */}
      <div className="mb-4 flex gap-2">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors",
              i < currentStepIndex ? "bg-green-main" : "bg-gray-200"
            )}
          />
        ))}
      </div>

      {/* Forced mode alert */}
      {isForcedMode && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-orange-50 p-3 text-sm text-orange-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Debes seleccionar una ubicación de destino para continuar</span>
        </div>
      )}

      {/* Step content */}
      <div className="mb-6 max-h-72 overflow-y-auto">
        {step === "country" && (
          <CountryStep
            filteredRegions={filteredRegions}
            selectedCountry={selectedCountry}
            onSelect={handleCountrySelect}
            isLoading={regionsLoading || availableLocations === undefined}
          />
        )}

        {step === "province" && (
          <ProvinceStep
            stateList={stateList}
            selectedStateIso={selectedStateIso}
            onSelect={handleStateChange}
          />
        )}

        {step === "municipality" && (
          <MunicipalityStep
            municipalityList={municipalityList}
            selectedMunicipality={selectedMunicipality}
            onSelect={setSelectedMunicipality}
          />
        )}
      </div>

      {/* Buttons */}
      <div className="flex flex-col gap-3">
        {step === "country" && !isForcedMode && (
          <button
            onClick={() => setRegionModalOpen(false)}
            className="border-green-main font-inter text-green-main hover:bg-green-main/10 flex-1 rounded-lg border-2 bg-transparent px-6 py-3 font-semibold transition-colors"
          >
            Cancelar
          </button>
        )}

        {step !== "country" && (
          <button
            onClick={() =>
              setStep(step === "municipality" ? "province" : "country")
            }
            className="border-green-main font-inter text-green-main hover:bg-green-main/10 flex-1 rounded-lg border-2 bg-transparent px-6 py-3 font-semibold transition-colors"
          >
            <span className="flex items-center justify-center gap-1">
              <ChevronLeft className="h-4 w-4" />
              Atrás
            </span>
          </button>
        )}

        {step === "country" && (
          <button
            onClick={() => setStep("province")}
            disabled={!canGoToProvince}
            className={cn(
              "font-inter flex-1 rounded-lg px-6 py-3 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              isForcedMode
                ? "bg-orange-main hover:bg-orange-600"
                : "bg-green-main hover:bg-[#047a2e]"
            )}
          >
            <span className="flex items-center justify-center gap-1">
              Continuar
              <ChevronRight className="h-4 w-4" />
            </span>
          </button>
        )}

        {step === "province" && (
          <button
            onClick={() =>
              isCuba ? setStep("municipality") : handleConfirm()
            }
            disabled={isCuba ? !canGoToMunicipality : !canConfirm}
            className={cn(
              "font-inter flex-1 rounded-lg px-6 py-3 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              isForcedMode
                ? "bg-orange-main hover:bg-orange-600"
                : "bg-green-main hover:bg-[#047a2e]"
            )}
          >
            <span className="flex items-center justify-center gap-1">
              {isCuba ? (
                <>
                  Continuar
                  <ChevronRight className="h-4 w-4" />
                </>
              ) : isForcedMode ? (
                "Comenzar a comprar"
              ) : (
                "Confirmar"
              )}
            </span>
          </button>
        )}

        {step === "municipality" && (
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={cn(
              "font-inter flex-1 rounded-lg px-6 py-3 font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50",
              isForcedMode
                ? "bg-orange-main hover:bg-orange-600"
                : "bg-green-main hover:bg-[#047a2e]"
            )}
          >
            {isForcedMode ? "Comenzar a comprar" : "Confirmar"}
          </button>
        )}
      </div>
    </Dialog>
  );
}

/* ---------- Sub-components ---------- */

function CountryStep({
  filteredRegions,
  selectedCountry,
  onSelect,
  isLoading,
}: {
  filteredRegions?: HttpTypes.StoreRegion[];
  selectedCountry: HttpTypes.StoreRegionCountry | null;
  onSelect: (
    region: HttpTypes.StoreRegion,
    country: HttpTypes.StoreRegionCountry
  ) => void;
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-14 animate-pulse rounded-lg bg-gray-200" />
        ))}
      </div>
    );
  }

  if (!filteredRegions?.length) {
    return (
      <div className="py-8 text-center text-gray-500">
        No hay ubicaciones disponibles
      </div>
    );
  }

  return (
    <div className="space-y-4 text-left">
      {filteredRegions.map((r) => (
        <div key={r.id} className="space-y-2">
          <h3 className="px-1 font-semibold text-gray-700">{r.name}</h3>
          <div className="grid grid-cols-1 gap-2">
            {r.countries?.map((c) => {
              const isSelected = selectedCountry?.iso_2 === c.iso_2;
              return (
                <button
                  key={c.id || c.iso_2}
                  onClick={() => onSelect(r, c)}
                  className={cn(
                    "font-inter flex w-full items-center justify-between rounded-lg border-2 px-4 py-3 text-left transition-all",
                    isSelected
                      ? "border-green-main bg-green-main/5"
                      : "border-transparent bg-white hover:bg-gray-50"
                  )}
                >
                  <div>
                    <div className="border-none font-medium text-gray-900">
                      {c.display_name || c.iso_2?.toUpperCase()}
                    </div>
                    {/* <div className="text-sm text-gray-500">
                      Moneda: {r.currency_code?.toUpperCase()}
                    </div> */}
                  </div>
                  {isSelected && <Check className="h-5 w-5 text-green-main" />}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProvinceStep({
  stateList,
  selectedStateIso,
  onSelect,
}: {
  stateList: { name: string; iso_code: string }[];
  selectedStateIso: string;
  onSelect: (isoCode: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="relative">
        <select
          value={selectedStateIso}
          onChange={(e) => onSelect(e.target.value)}
          className="border-green-main font-inter focus:ring-green-main/20 w-full appearance-none rounded-lg border-2 bg-white px-4 py-3 pr-10 text-gray-800 focus:ring-2 focus:outline-none"
        >
          <option value="">Selecciona una provincia/estado</option>
          {stateList.map((s) => (
            <option key={s.iso_code} value={s.iso_code}>
              {s.name}
            </option>
          ))}
        </select>
        <ChevronDown className="text-green-main pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2" />
      </div>
    </div>
  );
}

function MunicipalityStep({
  municipalityList,
  selectedMunicipality,
  onSelect,
}: {
  municipalityList: string[];
  selectedMunicipality: string;
  onSelect: (m: string) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="relative">
        <select
          value={selectedMunicipality}
          onChange={(e) => onSelect(e.target.value)}
          className="border-green-main font-inter focus:ring-green-main/20 w-full appearance-none rounded-lg border-2 bg-white px-4 py-3 pr-10 text-gray-800 focus:ring-2 focus:outline-none"
        >
          <option value="">Selecciona un municipio</option>
          {municipalityList.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <ChevronDown className="text-green-main pointer-events-none absolute top-1/2 right-3 h-5 w-5 -translate-y-1/2" />
      </div>
    </div>
  );
}
